import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ApiResponse } from '../models/api-response.model';
import { ClinicalEligibilityView } from '../models/clinical-eligibility.model';
import { DiureticAttestationPayload } from '../care-call-guide/models/diuretic-screen.util';

/**
 * BRD-CLIN-2026-006 §8.2 diuretic titration screening (welby-micro-service #972).
 *
 * BOTH ENDPOINTS ARE NOW WIRED. The POST went in with the guide fields that feed it, as this
 * docblock previously said it would — welby-firestore seeds the five §8.2 questions, and the guide
 * posts their answers when the MA confirms every criterion was reviewed.
 *
 * `.data` is unwrapped HERE, never in a component — the house rule, and the reason `AlertFlagsService`
 * documents which of its endpoints use the `ApiResponse<T>` envelope. This one does: the controller
 * carries `ResponseWrapperInterceptor`.
 */
@Injectable({
  providedIn: 'root',
})
export class ClinicalEligibilityService {
  private readonly baseUrl = `${environment.welbyEndpoint}/api/v1/core/patients`;

  constructor(private http: HttpClient) {}

  /**
   * The patient's §8.2 screening record, or `null` when it cannot be read.
   *
   * NULL ON FAILURE RATHER THAN AN ERROR, and this is a safety decision, not convenience. The single
   * consumer is the care call guide, which shows the gate to a clinician mid-call; a thrown error
   * there would either break the guide load or have to be swallowed at the call site. `null` routes
   * to exactly the same place `not_screened` does — `mayTitrate` is false for both — so a failed
   * read cannot read as permission. What it must NOT do is look like a clean screen, which is why
   * the caller renders "could not be read" rather than nothing at all.
   *
   * A 404 is not special-cased: the endpoint returns a `not_screened` view for a patient with no
   * record, so an actual 404 means something is wrong with the route, not with the patient.
   */
  getChfDiureticEligibility(patientId: string): Observable<ClinicalEligibilityView | null> {
    if (!patientId) {
      return of(null);
    }
    return this.http.get<ApiResponse<ClinicalEligibilityView>>(this.url(patientId)).pipe(
      map((res) => res?.data ?? null),
      catchError(() => of(null)),
    );
  }

  /**
   * Record a clinician's answer to the §8.2 screening questions.
   *
   * ERRORS ARE NOT SWALLOWED HERE, unlike the GET — and the asymmetry is the point. A failed READ
   * degrades safely: `null` routes exactly where `not_screened` does, so the gate stays closed and
   * the worst outcome is a banner saying the record could not be read. A failed WRITE is the
   * opposite: the clinician has answered the questions and believes the patient is screened, and
   * silently dropping it leaves the record saying nobody ever asked. The caller has to be able to
   * tell them, so this surfaces the failure.
   *
   * `screened_by` is deliberately absent from the body — the API takes clinical attribution from the
   * JWT, never from the request, and sending one would be rejected by `forbidNonWhitelisted`.
   */
  attestChfDiureticEligibility(
    patientId: string,
    body: DiureticAttestationPayload,
  ): Observable<ClinicalEligibilityView | null> {
    return this.http
      .post<ApiResponse<ClinicalEligibilityView>>(this.url(patientId), body)
      .pipe(map((res) => res?.data ?? null));
  }

  private url(patientId: string): string {
    return `${this.baseUrl}/${encodeURIComponent(patientId)}/clinical-eligibility/chf-diuretic-titration`;
  }
}
